import {yupResolver} from "@hookform/resolvers/yup"
import {
  Backdrop,
  Box,
  Dialog,
  DialogProps,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import DOMPurify from "dompurify"
import {FC, useState} from "react"
import {Controller, useForm} from "react-hook-form"
import {toast} from "react-toastify"
import * as yup from "yup"
import {Input, NewButton} from "../.."
import {CloseIcon, ErrorIcon, SuccessIcon} from "../../../assets/icons"
import {useConfigProvider} from "../../../config"
import {appInsights} from "../../../config/appInsights"
import {
  filterEmojis,
  stringIsLink,
  stringIsNotSQL,
  stringIsScript,
} from "../../../helpers"
import {
  accountEndpoints,
  ContactModalModel,
  notificationEndpoints,
} from "../../../services"
import type {Account, AccountTeam, Token} from "../../../types"
import {RichTextEditor} from "../../molecules"
import {useTranslation} from "react-i18next"
import useExpertBioStore from "../../../stores/expertBioStore"
import {decodeToken} from "react-jwt"
import useStorage, {StorageData} from "../../../hooks/useStorage"
import {CrmAccount} from "../../../types/CrmAccount"

const schema = yup.object({
  subject: yup
    .string()
    .required("Subject is required")
    .min(3, "Please provide a valid subject")
    .max(100, "Maximum character limit reached")
    .matches(stringIsLink, "Links are not allowed")
    .matches(stringIsScript, "Invalid text format")
    .matches(stringIsNotSQL, "Invalid text format"),
  description: yup
    .string()
    .required("Description is required")
    .min(3, "Please provide a valid description")
    .max(2000, "Maximum character limit reached")
    .matches(stringIsLink, "Links are not allowed")
    .matches(stringIsScript, "Invalid text format")
    .matches(stringIsNotSQL, "Invalid text format"),
})

type FormValues = yup.InferType<typeof schema>

type Props = {
  show: boolean
  handleToggle: () => void
  handleSuccess?: () => void
  member?: AccountTeam
  account?: Account
  accountTeam?: AccountTeam[]
}

const ContactModal: FC<Props> = (props) => {
  const {show, handleToggle, handleSuccess, member, account /*accountTeam*/} =
    props
  const {showFindExpert} = useExpertBioStore()

  const {sendContactModalEmail} = notificationEndpoints()
  const {user} = useConfigProvider()
  const theme = useTheme()
  const {t} = useTranslation()
  const {token} = useConfigProvider()
  const decoded = decodeToken<Token>(token)
  const storage = useStorage("session", StorageData.crmAccount)
  const accountNumber =
    decoded?.companyId === CrmAccount.Internal
      ? storage.get() || ""
      : decoded?.companyId || ""
  const [descriptionHtmlContent, setDescriptionHtmlContent] =
    useState<string>("")
  const queryClient = useQueryClient()

  const {reset, control, handleSubmit, setValue} = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onBlur",
  })

  const {getAccountTeamByAccountNumber} = accountEndpoints()

  const accountData = queryClient.getQueryData<Account>([
    "account",
    accountNumber,
  ])

  const getAccountTeamQuery = useQuery<AccountTeam[]>({
    queryKey: ["accountTeam", accountNumber],
    queryFn: () => getAccountTeamByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const sendNotificationMutation = useMutation<any, Error, ContactModalModel>({
    mutationFn: (payload) => sendContactModalEmail(payload),
    mutationKey: ["sendNotification"],
  })

  const handleClose = () => {
    handleToggle()
    reset()
    setValue("subject", "")
    setDescriptionHtmlContent("")
    setValue("description", "")
  }

  const handleCloseBackdrop: DialogProps["onClose"] = (event, reason) => {
    if (reason && reason === "backdropClick") return
    handleClose()
  }

  const handleSubmitForm = async (data: FormValues) => {
    const subject = DOMPurify.sanitize(data.subject)
    const filteredDescription = filterEmojis(descriptionHtmlContent)
    const description = DOMPurify.sanitize(filteredDescription || "")

    let mappedCCEmails = getAccountTeamQuery?.data?.map(
      (t) => t?.accountTeamEmailAddress,
    )
    mappedCCEmails?.push(accountData?.relationshipManagerEmail || "")
    /*const ccAddresses = [] as string[]

    if (
      account?.relationshipManagerEmail &&
      member?.accountTeamEmailAddress !== account?.relationshipManagerEmail
    ) {
      ccAddresses.push(account?.relationshipManagerEmail || "")
    }

    accountTeam?.forEach((teamMember) => {
      if (
        teamMember.accountTeamEmailAddress !== member?.accountTeamEmailAddress
      ) {
        ccAddresses.push(teamMember.accountTeamEmailAddress)
      }
    })*/

    const notificationPayload = {
      notificationType: showFindExpert
        ? "FindExpert-AccountTeam-ToClientAndTeam"
        : "AccountTeam-ToClientAndTeam",
      reference: "Contact Account Team",
      subject: subject,
      htmlContent: description,
      accountName: accountData?.crmAccountName || "",
      user: {
        userFirstname: user.firstname,
        userEmail: user.email,
        userOwnername: user.firstname,
      },
      toAddresses: member?.accountTeamEmailAddress
        ? [member.accountTeamEmailAddress]
        : [],
      ccAddresses: mappedCCEmails!!,
      expertName:
        member?.accountTeamFullName || member?.accountTeamEmailAddress || "",
    } satisfies ContactModalModel

    try {
      const notificationResponse = await sendNotificationMutation.mutateAsync(
        notificationPayload,
      )

      if (!!notificationResponse.status || !!notificationResponse.statusCode) {
        toast.error(
          t("An unexpected error occurred. Please try refresh your page."),
          {
            autoClose: 3000,
            icon: ErrorIcon,
            toastId: "api-error-toast",
          },
        )
        return
      }
      appInsights.trackEvent({
        name: "CONTACT_ACCOUNT_TEAM",
        properties: {
          userId: user.userId,
          userEmail: user.email,
          name: `${user.firstname} ${user.lastname}`,
          userName: user.userName,
          company: account?.accountNumber || member?.crmAccountNumber || "",
          accountName: account?.crmAccountName || member?.crmAccountName || "",
          expertName:
            member?.accountTeamFullName ||
            member?.accountTeamEmailAddress ||
            "",
        },
      })
      toast.success(t("Thank you, your message has been sent"), {
        autoClose: 3000,
        style: {
          borderLeftColor: "#58A733",
        },
        icon: SuccessIcon,
      })
      if (handleSuccess) {
        handleSuccess()
        reset()
        setValue("subject", "")
        setDescriptionHtmlContent("")
        setValue("description", "")
      } else {
        handleClose()
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Dialog
      open={show}
      onClose={handleCloseBackdrop}
      component="form"
      onSubmit={handleSubmit(handleSubmitForm)}
      slots={{backdrop: Backdrop}}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "brightness(0.99)",
          },
        },
      }}
      sx={{
        ".MuiDialog-paper": {
          maxWidth: "721px",
          marginTop: "40px",
          width: "712px",
          minWidth: 700,
          maxHeight: "100vh",
          bgcolor: "white",
          paddingTop: "17px",
          paddingBottom: "1.5rem",
          borderTop: "7px solid #003D50",
          borderRadius: "0.5rem",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "1rem",
        },
        ".css-b4bq0t-MuiPaper-root-MuiDialog-paper": {
          boxShadow: "none",
        },
      }}
    >
      <Stack
        sx={{width: "100%"}}
        component="header"
        direction="column"
        gap="1.25rem"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="end"
          paddingX="2rem"
        >
          <Typography
            fontSize="20px"
            lineHeight="1.2"
            fontWeight={600}
            color="#242d35"
          >
            {t("Contact_name", {name: member?.accountTeamFullName || ""})}
          </Typography>
          <NewButton
            variant="text"
            icon
            IconLeft={<CloseIcon />}
            onClick={handleClose}
            size="small"
            color="secondary"
            sx={{
              width: "24px",
              height: "24px",
              padding: "0",
              minWidth: "unset",
            }}
          />
        </Stack>
        <Box
          aria-label="divider"
          sx={{width: "100%", borderTop: "1px solid #CACDCD"}}
        />
      </Stack>
      <Stack
        sx={{width: "100%"}}
        component="main"
        direction="column"
        gap="0.25rem"
        paddingX="2rem"
      >
        <Stack direction="row" gap="1rem" alignItems="flex-start">
          <Controller
            control={control}
            name="subject"
            render={({field, fieldState}) => (
              <Input
                label="Subject"
                limit={100}
                required
                counter
                error={fieldState.error?.message}
                requiredStyle={{color: "#e83c4e"}}
                formGroupProps={{
                  gap: "0",
                  flex: 2,
                }}
                textFieldProps={{
                  placeholder: "Type subject",
                  fullWidth: true,
                  sx: {
                    ".MuiInputBase-formControl": {
                      height: "2.5rem",

                      input: {
                        fontFamily: '"Open Sans", sans-serif',
                        fontSize: "14px",
                        lineHeight: "1.5",
                        fontWeight: 400,
                        color: "#242d35",

                        "&::placeholder": {
                          color: "#7A7A79",
                          fontSize: "14px",
                          lineHeight: "1.5",
                          opacity: 1,
                        },
                      },
                    },
                  },
                  InputProps: {sx: {fontSize: "14px"}},
                  ...field,
                }}
                labelSx={{
                  fontSize: "1rem",
                  lineHeight: "1.5",
                  color: theme.palette.primary.main,
                  marginBottom: "0.25rem",
                }}
              />
            )}
          />
        </Stack>
        <Stack>
          <Controller
            control={control}
            name="description"
            render={({field, fieldState}) => (
              <RichTextEditor
                formError={
                  fieldState.error?.message ? t(fieldState.error?.message) : ""
                }
                {...field}
                onChange={(value) => {
                  setDescriptionHtmlContent(value.html)
                  field.onChange(value.text)
                }}
                placeholder={t("Tell us about your enquiry")}
                label={t("Description")}
                editorContainerStyle={{height: "289px"}}
                requiredStyle={{color: "#e83c4e"}}
                labelProps={{
                  sx: {
                    fontSize: "1rem",
                    lineHeight: "1.5",
                    color: theme.palette.primary.main,
                  },
                }}
              />
            )}
          />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        gap="5px"
        component="footer"
        marginTop="0.25rem"
        justifyContent="flex-end"
        width="100%"
        paddingX="2rem"
      >
        <NewButton
          variant="outline"
          color="primary"
          size="medium"
          text={t("Cancel")}
          onClick={handleClose}
          sx={{
            minWidth: "88px",
            backgroundColor: "white",
            color: "#053747",
            textDecoration: "underline",
            textUnderlineOffset: "5px",
            border: "unset",
            "&:hover": {
              borderBottomWidth: "2px",
              bgcolor: "transparent",
              paddingTop: "2px",
              textDecoration: "underline",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
          }}
        />
        <NewButton
          variant="filled"
          color="primary"
          size="medium"
          text={t("Submit")}
          type="submit"
          loading={sendNotificationMutation.isPending}
          sx={{
            width: "136px",
          }}
        />
      </Stack>
    </Dialog>
  )
}

ContactModal.displayName = "ContactAccountTeamModal"

export default ContactModal
